import { useEffect, useMemo, useRef, useState } from 'react';
import type { Selection } from '@react-types/shared';
import { OnChangeFn, PaginationState } from '@tanstack/react-table';
import { ContentWrapper } from '@/widgets/ContentWrapper';
import {
  useDeleteNotificationMutation,
  useGetNotificationsQuery,
  useMarkNotificationAsReadMutation,
  type INotification,
  type NotificationsSortType,
} from '@/entities/Notifications';
import { ContainerTable } from '@/shared/ui/AppTable';
import { CustomInput, SelectSort } from '@/shared/ui/Fields';
import { Section } from '@/shared/ui/Section';
import { MessageModal } from '@/shared/ui/Modal';
import { Svg } from '@/shared/ui/Svg';
import { useDebounce } from '@/shared/lib/hooks/useDebounce';
import { usePageTitle } from '@/shared/lib/hooks';
import { getErrorMessage } from '@/shared/lib/utils/getErrorMessage';
import SearchIcon from '@/shared/assets/icons/search.svg';
import { pushNotificationsColumns } from '../../config/pushNotificationsColumns';
import { pushNotificationsSort } from '../../config/pushNotificationsSort';
import { PushNotificationDetailModal } from '../../components/PushNotificationDetailModal';
import styles from './PushNotificationsView.module.scss';

const PAGE_SIZE = 8;

export const PushNotificationsView = () => {
  usePageTitle('Push Notifications');

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<NotificationsSortType>('createdAt:desc');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 1,
    pageSize: PAGE_SIZE,
  });
  const [selectedNotification, setSelectedNotification] = useState<INotification | null>(null);
  const [notificationToDelete, setNotificationToDelete] = useState<INotification | null>(null);

  const debouncedSearch = useDebounce(search.trim(), 300);
  const prevSearchRef = useRef(debouncedSearch);

  useEffect(() => {
    if (prevSearchRef.current !== debouncedSearch) {
      prevSearchRef.current = debouncedSearch;
      setPagination((prev) => ({ ...prev, pageIndex: 1 }));
    }
  }, [debouncedSearch]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 1 }));
  }, [sort]);

  const { data, isLoading, isFetching, error } = useGetNotificationsQuery({
    page: pagination.pageIndex,
    pageSize: PAGE_SIZE,
    search: debouncedSearch || undefined,
    sort,
  });

  const [markNotificationAsRead] = useMarkNotificationAsReadMutation();
  const [deleteNotification, { isLoading: isDeleting }] = useDeleteNotificationMutation();

  const notifications = useMemo(() => data?.notifications ?? [], [data?.notifications]);

  const tableMeta = useMemo(
    () =>
      data?.pagination
        ? {
            page: data.pagination.page,
            pageSize: data.pagination.pageSize,
            total: data.pagination.total,
            pageCount: data.pagination.pageCount,
          }
        : undefined,
    [data],
  );

  const handleSortChange = (keys: Selection) => {
    const selectedValue = new Set(keys).values().next().value;
    setSort((selectedValue as NotificationsSortType) || 'createdAt:desc');
  };

  const handleFilter = () => {
    // TODO: filter modal when API supports filters
  };

  const handleNotificationClick = (notification: INotification) => {
    setSelectedNotification(notification.isRead ? notification : { ...notification, isRead: true });

    if (!notification.isRead) {
      markNotificationAsRead(notification.id);
    }
  };

  const handleDeleteRequest = (notification: INotification) => {
    setNotificationToDelete(notification);
  };

  const handleConfirmDelete = async () => {
    if (!notificationToDelete) return;

    try {
      await deleteNotification(notificationToDelete.id).unwrap();

      if (selectedNotification?.id === notificationToDelete.id) {
        setSelectedNotification(null);
      }

      setNotificationToDelete(null);
    } catch {
      // keep confirm open on error
    }
  };

  return (
    <Section typeSection="section" className="flex flex-col gap-6">
      <h2 className="text-[32px] font-medium">Push Notifications</h2>

      <div className="max-w-[530px]">
        <CustomInput
          startContent={<Svg Icon={SearchIcon} />}
          placeholder="Search"
          value={search}
          onValueChange={setSearch}
        />
      </div>

      <div className="flex items-center justify-end gap-2">
        {/*<Button isIconOnly className="bg-[#211D55]" onPress={handleFilter} aria-label="Filter">*/}
        {/*  <Svg Icon={FilterIcon} />*/}
        {/*</Button>*/}
        <SelectSort
          sections={pushNotificationsSort}
          defaultValue={sort}
          onSelectionChange={handleSortChange}
        />
      </div>

      <ContentWrapper
        isLoading={isLoading}
        isError={!!error}
        errorMessage={getErrorMessage(error) ?? undefined}
        isEmpty={!isLoading && !isFetching && notifications.length === 0}
        emptyMessage="No notifications found."
      >
        <ContainerTable<INotification>
          caption="Push notifications"
          data={notifications}
          columns={pushNotificationsColumns(handleDeleteRequest)}
          pagination={pagination}
          setPagination={setPagination as OnChangeFn<PaginationState>}
          meta={tableMeta}
          hideTopActions
          onRowClick={handleNotificationClick}
          getRowClassName={(notification) => (!notification.isRead ? styles.unreadRow : undefined)}
        />
      </ContentWrapper>

      <PushNotificationDetailModal
        notification={selectedNotification}
        onClose={() => setSelectedNotification(null)}
      />

      <MessageModal
        isOpen={!!notificationToDelete}
        onOpenChange={() => setNotificationToDelete(null)}
        title="Delete notification"
        text="Are you sure you want to delete this notification?"
        confirmText="Delete"
        variant="red"
        isLoading={isDeleting}
        toggleVisibility={handleConfirmDelete}
      />
    </Section>
  );
};
